import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import React from 'react';

import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useTranslator } from '@/hooks/useTranslator';
import { CurrencyCodeType } from '@/types/currency';

interface Props {
  readonly marketExchangeRate?: string | null;
  readonly conversionRate?: string | null;
  readonly effectiveTransactionValue?: string | null;
  readonly fromCurrency: CurrencyCodeType;
  readonly toCurrency: CurrencyCodeType;
  readonly calizaExchangeSpreadFeePercentage?: string | null;
}

const MAJOR_CURRENCIES = [CurrencyCodeType.USD, CurrencyCodeType.USDC, CurrencyCodeType.USDT];

const RATE_FORMATTER_CONFIG = {
  maximumFractionDigits: 7,
  minimumFractionDigits: 7,
};

interface FormatRateParams {
  rate: string | null | undefined;
  fromCurrency: CurrencyCodeType;
  toCurrency: CurrencyCodeType;
  rateFormatter: Intl.NumberFormat;
}

const formatRate = ({
  rate,
  fromCurrency,
  toCurrency,
  rateFormatter,
}: FormatRateParams): string => {
  if (
    !rate ||
    Number(rate) === 0 ||
    (MAJOR_CURRENCIES.includes(fromCurrency) && MAJOR_CURRENCIES.includes(toCurrency))
  ) {
    return `1 ${fromCurrency} = 1 ${toCurrency}`;
  }

  if (MAJOR_CURRENCIES.includes(toCurrency)) {
    const invertedRate: number = 1 / Number(rate);
    return `1 ${toCurrency} = ${rateFormatter.format(invertedRate)} ${fromCurrency}`;
  }

  return `1 ${fromCurrency} = ${rateFormatter.format(Number(rate))} ${toCurrency}`;
};

export const ConversionRatesInfo: React.FC<Props> = (props: Props): React.ReactElement | null => {
  const {
    marketExchangeRate,
    conversionRate,
    effectiveTransactionValue,
    fromCurrency,
    toCurrency,
    calizaExchangeSpreadFeePercentage,
  } = props;
  const tr = useTranslator();
  const rateFormatter = useLocalizedFormatter(RATE_FORMATTER_CONFIG);

  const midMarketRateFormatted = React.useMemo((): string | null => {
    return formatRate({ rate: marketExchangeRate, fromCurrency, toCurrency, rateFormatter });
  }, [marketExchangeRate, fromCurrency, toCurrency, rateFormatter]);

  const conversionRateFormatted = React.useMemo((): string => {
    return formatRate({ rate: conversionRate, fromCurrency, toCurrency, rateFormatter });
  }, [conversionRate, fromCurrency, rateFormatter, toCurrency]);

  const formattedVET = React.useMemo((): string | null => {
    const isBRLOperation =
      fromCurrency === CurrencyCodeType.BRL || toCurrency === CurrencyCodeType.BRL;

    if (!isBRLOperation) {
      return null;
    }

    return formatRate({ rate: effectiveTransactionValue, fromCurrency, toCurrency, rateFormatter });
  }, [effectiveTransactionValue, fromCurrency, rateFormatter, toCurrency]);

  const tooltipContent = React.useMemo((): React.ReactElement | null => {
    const tooltipItems: string[] = [];

    if (calizaExchangeSpreadFeePercentage) {
      tooltipItems.push(tr('CONVERSION_RATE_TOOLTIP', calizaExchangeSpreadFeePercentage));
    }

    if (formattedVET) {
      tooltipItems.push(tr('VET_TOOLTIP'));
    }

    if (tooltipItems.length === 0) {
      return null;
    }

    if (tooltipItems.length === 1) {
      return <Typography>{tooltipItems.at(0)}</Typography>;
    }

    return (
      <Stack component="ul" sx={styles.tooltip}>
        {tooltipItems.map(
          (item: string, index: number): React.ReactElement => (
            <Typography key={index} component="li">
              {item}
            </Typography>
          ),
        )}
      </Stack>
    );
  }, [calizaExchangeSpreadFeePercentage, formattedVET, tr]);

  if (!midMarketRateFormatted && !conversionRate && !formattedVET) {
    return null;
  }

  return (
    <Box sx={styles.container}>
      <Stack direction="column" gap={0.5} flex={1}>
        {midMarketRateFormatted && (
          <Typography variant="caption">
            {tr('MID_MARKET_RATE')}: {midMarketRateFormatted} (source: XE)
          </Typography>
        )}
        <Typography variant="caption">
          {tr('CONVERSION_RATE')}: {conversionRateFormatted}
        </Typography>
        {formattedVET && (
          <Typography variant="caption">
            {tr('VET')}: {formattedVET}
          </Typography>
        )}
      </Stack>
      {tooltipContent && (
        <Tooltip title={tooltipContent} placement="top">
          <Box width={20} height={20}>
            <InfoIcon fill="currentColor" width={20} height={20} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    py: 2,
    px: 2.5,
    borderRadius: 4,
    color: 'custom.LightGray',
    bgcolor: 'custom.Primary1Background',
  },
  tooltip: {
    margin: 0,
    paddingLeft: 2,
    gap: 1,
  },
};
